import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../../environments/environment';
import {
    AnalyticsDefinition,
    CameraAnalyticsConfigResponse,
    CameraCorrelationSettings,
    ProviderEvent,
    VehicleTransaction,
    VisionContext,
    VisionPolicy,
} from '../../models/vte';

export type PaginatedResponse<T> = {
    count: number;
    next: string | null;
    previous: string | null;
    results: T[];
};

export type VehicleTransactionListParams = {
    page?: number;
    page_size?: number;
    search?: string;
    status?: string;
    status_group?: string;
    camera_id?: number | string;
    ordering?: string;
    org_id?: number | string;
};

export type ProviderEventListParams = {
    page?: number;
    page_size?: number;
    provider_id?: string;
    processing_status?: string;
    camera_id?: number | string;
    search?: string;
    ordering?: string;
    org_id?: number | string;
};

export type CameraAnalyticsConfigPatchPayload = {
    analytics_configurations: Array<{
        analytics_definition: number;
        is_enabled: boolean;
    }>;
};

@Injectable({
    providedIn: 'root',
})
export class VteApiService {
    private readonly baseUrl = environment.baseUrl;

    constructor(private http: HttpClient) {
    }

    getVisionContext(organizationId: number | string): Observable<VisionContext> {
        return this.http.get<VisionContext>(
            `${this.baseUrl}organizations/${organizationId}/vision-context/`,
        );
    }

    getVisionPolicy(organizationId: number | string): Observable<VisionPolicy> {
        return this.http.get<VisionPolicy>(
            `${this.baseUrl}organizations/${organizationId}/vision-policy/`,
        );
    }

    patchVisionPolicy(
        organizationId: number | string,
        payload: Partial<VisionPolicy>,
    ): Observable<VisionPolicy> {
        return this.http.patch<VisionPolicy>(
            `${this.baseUrl}organizations/${organizationId}/vision-policy/`,
            payload,
        );
    }

    listAnalyticsDefinitions(): Observable<AnalyticsDefinition[]> {
        return this.http.get<AnalyticsDefinition[]>(`${this.baseUrl}analytics-definitions/`);
    }

    getCameraAnalyticsConfig(cameraId: number | string): Observable<CameraAnalyticsConfigResponse> {
        return this.http.get<CameraAnalyticsConfigResponse>(
            `${this.baseUrl}cameras/${cameraId}/analytics-config/`,
        );
    }

    patchCameraAnalyticsConfig(
        cameraId: number | string,
        payload: CameraAnalyticsConfigPatchPayload,
    ): Observable<CameraAnalyticsConfigResponse> {
        return this.http.patch<CameraAnalyticsConfigResponse>(
            `${this.baseUrl}cameras/${cameraId}/analytics-config/`,
            payload,
        );
    }

    createCameraAnalyticsOverride(cameraId: number | string): Observable<CameraAnalyticsConfigResponse> {
        return this.http.post<CameraAnalyticsConfigResponse>(
            `${this.baseUrl}cameras/${cameraId}/analytics-config/`,
            {},
        );
    }

    resetCameraAnalyticsOverride(cameraId: number | string): Observable<CameraAnalyticsConfigResponse> {
        return this.http.delete<CameraAnalyticsConfigResponse>(
            `${this.baseUrl}cameras/${cameraId}/analytics-config/`,
        );
    }

    getCameraCorrelationSettings(cameraId: number | string): Observable<CameraCorrelationSettings> {
        return this.http.get<CameraCorrelationSettings>(
            `${this.baseUrl}cameras/${cameraId}/correlation-settings/`,
        );
    }

    patchCameraCorrelationSettings(
        cameraId: number | string,
        payload: Partial<CameraCorrelationSettings>,
    ): Observable<CameraCorrelationSettings> {
        return this.http.patch<CameraCorrelationSettings>(
            `${this.baseUrl}cameras/${cameraId}/correlation-settings/`,
            payload,
        );
    }

    listVehicleTransactions(
        params: VehicleTransactionListParams = {},
    ): Observable<PaginatedResponse<VehicleTransaction>> {
        return this.http.get<PaginatedResponse<VehicleTransaction>>(
            `${this.baseUrl}vehicle-transactions/`,
            {params: this.toParams(params)},
        );
    }

    getVehicleTransaction(transactionId: string): Observable<VehicleTransaction> {
        return this.http.get<VehicleTransaction>(
            `${this.baseUrl}vehicle-transactions/${transactionId}/`,
        );
    }

    listProviderEvents(
        params: ProviderEventListParams = {},
    ): Observable<PaginatedResponse<ProviderEvent>> {
        return this.http.get<PaginatedResponse<ProviderEvent>>(
            `${this.baseUrl}provider-events/`,
            {params: this.toParams(params)},
        );
    }

    getProviderEvent(
        eventId: number | string,
        providerId?: string,
    ): Observable<ProviderEvent> {
        let params = new HttpParams();
        if (providerId) {
            params = params.set('provider_id', providerId);
        }
        return this.http.get<ProviderEvent>(`${this.baseUrl}provider-events/${eventId}/`, {params});
    }

    private toParams(values: Record<string, unknown>): HttpParams {
        let params = new HttpParams();
        Object.entries(values).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                params = params.set(key, String(value));
            }
        });
        return params;
    }
}
